/**
 * Hook for loading inspection template structure
 * 
 * Fetches template systems and items, sorted by order
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface InspectionSystem {
  system_key: string;
  name: string;
  description: string | null;
  sort_order: number;
  group_key: string | null;
}

export interface InspectionTemplateItem {
  id: string;
  template_id: string;
  system_key: string;
  item_key: string;
  shared_key: string | null;
  name: string;
  description: string | null;
  is_required: boolean;
  sort_order: number;
  severity_policy: {
    allowed: string[];
    default: string;
  };
  evidence_requirements: Record<string, string>;
  result_schema: Record<string, unknown> | null;
  ai_policy: Record<string, unknown> | null;
}

export interface GroupedTemplateItems {
  system: InspectionSystem;
  items: InspectionTemplateItem[];
}

interface UseInspectionTemplateResult {
  /** All systems for this template */
  systems: InspectionSystem[];
  /** All items for this template */
  items: InspectionTemplateItem[];
  /** Items grouped by system */
  groupedItems: GroupedTemplateItems[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
}

/**
 * Hook to load template structure (systems and items)
 * 
 * @param templateId - The template ID to load
 */
export function useInspectionTemplate(
  templateId: string | undefined
): UseInspectionTemplateResult {
  // Fetch template systems
  const {
    data: templateSystems,
    isLoading: systemsLoading,
    error: systemsError,
  } = useQuery({
    queryKey: ['inspection-template-systems', templateId],
    queryFn: async () => {
      if (!templateId) return [];

      const { data, error } = await supabase
        .from('inspection_template_systems')
        .select('system_key, sort_order')
        .eq('template_id', templateId)
        .order('sort_order');

      if (error) throw error;
      return data;
    },
    enabled: !!templateId,
  });

  // Fetch canonical system info
  const systemKeys = templateSystems?.map(s => s.system_key) || [];
  const {
    data: canonicalSystems,
    isLoading: canonicalLoading,
  } = useQuery({
    queryKey: ['inspection-systems', systemKeys],
    queryFn: async () => {
      if (systemKeys.length === 0) return [];

      const { data, error } = await supabase
        .from('inspection_systems')
        .select('system_key, name, description, sort_order, group_key')
        .in('system_key', systemKeys)
        .eq('is_active', true);

      if (error) throw error;
      return data as InspectionSystem[];
    },
    enabled: systemKeys.length > 0,
  });

  // Fetch template items with system info
  const {
    data: itemsData,
    isLoading: itemsLoading,
    error: itemsError,
  } = useQuery({
    queryKey: ['inspection-template-items', templateId],
    queryFn: async () => {
      if (!templateId) return [];

      const { data, error } = await supabase
        .from('inspection_template_items')
        .select(`
          *,
          inspection_template_systems!inner(system_key)
        `)
        .eq('template_id', templateId)
        .order('sort_order');

      if (error) throw error;
      
      // Map items to add system_key from joined data
      return (data || []).map(item => ({
        ...item,
        system_key: (item.inspection_template_systems as any)?.system_key || 'unknown',
        // Normalize JSON fields
        severity_policy: item.severity_policy || { allowed: ['PASS', 'CAUTION', 'FAIL', 'NA'], default: 'NA' },
        evidence_requirements: item.evidence_requirements || {},
      })) as unknown as InspectionTemplateItem[];
    },
    enabled: !!templateId,
  });

  // Build systems list with proper ordering from template_systems
  const systems: InspectionSystem[] = (templateSystems || [])
    .map(ts => {
      const canonical = canonicalSystems?.find(cs => cs.system_key === ts.system_key);
      return canonical ? { ...canonical, sort_order: ts.sort_order } : null;
    })
    .filter((s): s is InspectionSystem => s !== null)
    .sort((a, b) => a.sort_order - b.sort_order);

  // Use itemsData for items
  const items = itemsData || [];

  // Group items by system
  const groupedItems: GroupedTemplateItems[] = systems.map(system => ({
    system,
    items: items
      .filter(item => item.system_key === system.system_key)
      .sort((a, b) => a.sort_order - b.sort_order),
  }));

  const isLoading = systemsLoading || canonicalLoading || itemsLoading;
  const error = systemsError || itemsError;

  return {
    systems,
    items,
    groupedItems,
    isLoading,
    error: error as Error | null,
  };
}
